import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Ship, Loader2 } from "lucide-react";
import { API } from "../context/CartContext";
import { SectionTitle } from "./Sections";

const inputCls = "w-full px-5 py-3 rounded-2xl border border-brand-green/25 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange";

export const QuotationSection = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", country: "", quantity_kg: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.quantity_kg) < 100) { toast.error(t("quote.minQty")); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/quotation`, {
        name: form.name, company: form.company || null, email: form.email,
        phone: form.phone || null, country: form.country,
        quantity_kg: Number(form.quantity_kg), message: form.message || null,
      });
      toast.success(t("quote.success"));
      setForm({ name: "", company: "", email: "", phone: "", country: "", quantity_kg: "", message: "" });
    } catch {
      toast.error(t("quote.error"));
    } finally { setBusy(false); }
  };

  return (
    <section id="quotation" className="bg-brand-cream py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <SectionTitle title={t("quote.title")} subtitle={t("quote.subtitle")} />
          <div className="hidden lg:flex w-20 h-20 rounded-3xl bg-brand-yellow items-center justify-center text-brand-dark -mt-6">
            <Ship size={36} />
          </div>
        </div>
        <motion.form onSubmit={submit}
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="lg:col-span-7 bg-white rounded-3xl p-8 border border-brand-green/15 shadow-[0_8px_30px_rgba(109,76,65,0.08)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder={t("quote.name")} className={inputCls} style={{ background: "#FFF9E6" }} />
            <input data-testid="quote-company-input" value={form.company} onChange={set("company")} placeholder={t("quote.company")} className={inputCls} style={{ background: "#FFF9E6" }} />
            <input data-testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t("quote.email")} className={inputCls} style={{ background: "#FFF9E6" }} />
            <input data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder={t("quote.phone")} className={inputCls} style={{ background: "#FFF9E6" }} />
            <input data-testid="quote-country-input" required value={form.country} onChange={set("country")} placeholder={t("quote.country")} className={inputCls} style={{ background: "#FFF9E6" }} />
            <input data-testid="quote-qty-input" type="number" min="100" required value={form.quantity_kg} onChange={set("quantity_kg")} placeholder={t("quote.qty")} className={inputCls} style={{ background: "#FFF9E6" }} />
          </div>
          <textarea data-testid="quote-message-input" rows="3" value={form.message} onChange={set("message")} placeholder={t("quote.message")}
            className={`${inputCls} mt-4 resize-none`} style={{ background: "#FFF9E6" }} />
          <button data-testid="quote-submit-button" type="submit" disabled={busy}
            className="mt-5 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Ship size={18} />} {t("quote.submit")}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
